import { useCallback, useEffect, useMemo, useState } from 'react';
import { IEntityPickerProps } from './EditorEntityPicker';
import { getEntityByID } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/Folder/FolderService';
import { PickerActions } from '@decisions/server/DecisionsFramework/Design/Properties/Attributes';
import { getEditorSettings } from 'components/property-editors/utils/EditorConfigurationUtils';
import { IPropertyChild } from 'components/property-editors/utils/ProviderNodeUtils';

export function useEpickEditor({ child, path, onChange, getEntityLabel, canCreate, canEdit }: IEntityPickerProps) {
  const [entityName, setEntityName] = useState('');
  const [allowEdit, setAllowEdit] = useState(!!canEdit);
  const [entityValue, setEntityValue] = useState<object | string | null>(null);

  // update value when it was changed outside from component
  useEffect(() => {
    setEntityValue(child.Value && child.Value.OutputValue);
  }, [child.Value]);

  const onValueChange = useCallback(
    (value: object | string | null) => {
      setEntityValue(value);
      onChange(value, path);
    },
    [path, onChange]
  );

  const actionsText: string = useMemo(() => {
    return extractPickerActionsText(child);
  }, [child]);

  const getEntityLabelById = useCallback(async (): Promise<string> => {
    // we know what entityValue shoud be as 'string' in default case
    if (entityValue && typeof entityValue === 'string') {
      const entityHeader = await getEntityByID({ entityID: entityValue });

      if (!entityHeader) {
        setAllowEdit(false);
        return `[Flow not found with flowId: ${entityValue}]`;
      } else {
        setAllowEdit(!!canEdit);
        return entityHeader.EntityName;
      }
    } else {
      return '';
    }
  }, [entityValue, canEdit]);

  const updateEntityName = useCallback(async () => {
    let name: string;
    if (getEntityLabel) {
      name = await getEntityLabel(entityValue as string | object);
    } else {
      // use default case
      name = await getEntityLabelById();
    }
    setEntityName(name);
  }, [entityValue, getEntityLabel, getEntityLabelById]);

  // set entity name
  useEffect(() => {
    updateEntityName();
  }, [updateEntityName]);

  const hasNodeValue = child.Value && child.Value.OutputValue;

  const showEdit: boolean =
    allowEdit && !!entityValue && !!hasNodeValue && hasFlag(actionsText, [PickerActions.Edit, PickerActions.All]);
  const showCreate: boolean = !!canCreate && hasFlag(actionsText, [PickerActions.Create, PickerActions.All]);
  const showPick: boolean = hasFlag(actionsText, [PickerActions.Pick, PickerActions.Default, PickerActions.All]);
  const showReset: boolean = hasFlag(actionsText, [PickerActions.Reset, PickerActions.Default, PickerActions.All]);

  return { entityName, allowEdit, onValueChange, showEdit, showCreate, showPick, showReset };
}

function hasFlag(actions: string, flagsToCheck: PickerActions[]) {
  return flagsToCheck.filter(flag => actions.indexOf(PickerActions[flag]) > -1).length > 0;
}

function extractPickerActionsText(child: IPropertyChild): string {
  const result = getEditorSettings<any>(child).ActionsText as string;

  return result || PickerActions[PickerActions.None];
}
